import { documentService } from '../services/documentService';
import reminderService from '../services/reminderService';

// "Delete and forget" (#79): batch File Manager deletions run in the background so
// the user is never tethered to a progress bar — they can navigate away / close
// the File Manager immediately, mirroring the non-blocking Upload flow. If the
// File Manager is no longer open by the time the deletion finishes, a Bell
// notification announces completion; if the user stayed, no notification (the list
// just refreshes).

// How many File Manager instances are currently mounted. The runner reads this at
// COMPLETION time to decide whether the user has left.
let openFileManagers = 0;

export function registerFileManagerOpen() {
    openFileManagers += 1;
    return () => { openFileManagers = Math.max(0, openFileManagers - 1); };
}

export function isFileManagerOpen() {
    return openFileManagers > 0;
}

function emit(name, detail) {
    try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch (_) { /* no-op */ }
}

/**
 * Fire-and-forget batch delete that is independent of the React component
 * lifecycle (a module function, so it keeps running after the File Manager
 * unmounts). Resolves recursive folder contents, deletes every document then
 * folder, emits progress so a live view can show a non-blocking indicator, and —
 * if no File Manager is open when it finishes — posts a Bell reminder.
 *
 * Returns a promise resolving to the number of items processed (callers may
 * ignore it; the UI is driven by the emitted events).
 */
export async function runBackgroundDelete({ documentIds = [], folderIds = [], mode = 'permanent' }) {
    // `mode`:
    //   'permanent' → hard delete (the historic behaviour; used when the retention
    //                 policy is "immediately", or for Delete Permanently).
    //   'trash'     → soft delete documents into Recently Deleted (#126). Folders
    //                 are emptied via move_to_parent so their (now-trashed) docs
    //                 survive and can be restored; the empty folder is removed.
    const trash = mode === 'trash';

    // Expand each selected folder into its documents (the folder row + cascade
    // removes the subfolders); collect the root folders to delete last.
    let docIds = [...documentIds];
    const folders = [];
    for (const fid of folderIds) {
        try {
            const contents = await documentService.getFolderContentsRecursive(fid);
            docIds = docIds.concat((contents.documents || []).map((d) => d.id));
        } catch (_) { /* still delete the folder itself below */ }
        folders.push(fid);
    }
    docIds = Array.from(new Set(docIds.filter(Boolean)));

    const total = docIds.length + folders.length;
    let done = 0;
    const tick = () => { done += 1; emit('omnis:file-delete-progress', { done, total }); };
    emit('omnis:file-delete-progress', { done: 0, total });

    for (const id of docIds) {
        try {
            if (trash) await documentService.trashDocument(id);
            else await documentService.deleteDocument(id);
        } catch (_) { /* keep going — one failure shouldn't abort the batch */ }
        tick();
        // NB: we deliberately do NOT emit a per-file `omnis:document-deleted` here —
        // for a 100-file batch that would trigger 100 list reloads. The File Manager
        // refreshes once on `omnis:file-delete-complete` (and the toast tracks
        // progress meanwhile).
    }
    for (const fid of folders) {
        // In trash mode we move the folder's (already-trashed) docs up to the parent
        // instead of cascade-deleting them, so nothing is destroyed.
        try { await documentService.deleteFolder(fid, trash ? 'move_to_parent' : 'cascade'); } catch (_) { /* keep going */ }
        tick();
    }

    emit('omnis:file-delete-complete', { total });

    // The user navigated away before this finished → tell them via the Bell.
    // Notification Bug (#346): this is an AUTOMATED system notice, not
    // something the user asked to be reminded about, so it is minted as
    // `kind: 'system'` — it renders as a "System Notification" (never the
    // Reminder icon/label) and stays out of the Notes & Reminders module.
    if (!isFileManagerOpen() && total > 0) {
        try {
            await reminderService.createReminder({
                title: `Finished deleting ${total} item${total === 1 ? '' : 's'} from your files.`,
                remind_at: new Date().toISOString(),
                kind: 'system',
            });
            emit('omnis:reminder-created', {});
        } catch (_) { /* non-fatal — the files are already gone */ }
    }
    return total;
}
